/**
 * One canonical treatment of the game master's narration markup.
 *
 * This used to exist four times over — in GameBoard, GameIntroScreen, TurnBook and (server-side)
 * TurnManager — with four different regexes and therefore four different behaviours on the same
 * text. Three of them capped a tag at 30 characters, so a marker like
 * `[voice:Верховный Магистр Ордена]` was too long to match and leaked onto the screen and into the
 * screen reader as literal text. A fourth matched `[voice:[a-z_]+]`, which does not cover Cyrillic
 * at all and only worked because a later rule happened to catch the leftovers.
 */

/** A speaker marker: `[voice:Имя]…[/voice]`. The name is any script, any length. */
const VOICE_MARKER = /\[voice:[^\]]*\]|\[\/voice\]/gi;

/**
 * A delivery cue: `[whispers]`, `[dramatically]`. Uncapped in length AND allowed to span
 * newlines — both restrictions were bugs. A long name outlived the length cap; a tag the game
 * master wrapped across two lines outlived the newline exclusion. Either way the markup reached
 * the screen reader and the speech engine as literal text.
 */
const DELIVERY_TAG = /\[[^\]]*\]/g;

const MARKDOWN = /(\*{1,3}|`|^#{1,6}\s*|^>\s*|_{2})/gm;

/**
 * Narration as a human should read or hear it: markup gone, spacing tidy.
 *
 * Used for the screen, for the screen reader and for the printed book, so all three agree. The
 * speech engine gets the *unstripped* text — the markup is what tells it who speaks each line.
 */
export function stripMarkup(text?: string | null): string {
  return (text || '')
    .replace(VOICE_MARKER, ' ')
    .replace(DELIVERY_TAG, ' ')
    .replace(MARKDOWN, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([,.!?;:…])/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * True when the text still contains markup after stripping.
 *
 * Exported so a test can assert the property directly rather than comparing against a fixture:
 * no marker of any name length, in any script, may survive.
 */
export function hasMarkup(text?: string | null): boolean {
  return /\[[^\]\n]*\]/.test(text || '');
}

/**
 * BCP-47 tag for the browser's own speech synthesis, from the app's i18n language code.
 *
 * Three components hardcoded `ru-RU` while the interface ships in eight languages, so a player on
 * Spanish or German had their text read aloud by a Russian voice — unintelligible, and it landed
 * on exactly the users who depend on speech most. The fallback path matters more than usual now:
 * languages with no offline engine of their own are read by the browser.
 */
const SPEECH_REGION: Record<string, string> = {
  ru: 'ru-RU', uk: 'uk-UA', en: 'en-US', es: 'es-ES',
  de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', zh: 'zh-CN',
};

export function speechLang(language?: string | null): string {
  const code = (language || '').split('-')[0].toLowerCase();
  // An unlisted language is better served by the browser's own default than by a wrong voice.
  return SPEECH_REGION[code] || language || 'en-US';
}
